import React from 'react';
import PropTypes, { bool } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import Button from '../Button';
import { Card } from 'reactstrap';
import { FaRegCheckCircle } from 'react-icons/fa';
import { format } from 'date-fns';
import { translate } from '../../components/Translate';
import { RulesName, RulesType } from '../../enums';
import {formatPricesByCountry} from "../../utils/products";
import { isEssentialPlan } from '../../utils/validations';
import { isDescriptionLong } from '../../utils/descriptions';

const carouselItemClass = 'carouselItem'
const planClass = 'MembershipCard'

export const AddOnItem = ({ intl, item, applyTrial, onClick, client, price, availableSalePriceOffer }) => {
  const { isBlocked, isSelected } = item;
  const hasCompactDescription = isDescriptionLong(item?.metadata?.description);
  return (
    <Card
      key={item?.productID}
      style={{background: 'linear-gradient(0deg,#141c27 19%,#141c27 52%,#141c27 93%,#141c27)', color: 'white'}}
      className={classNames('MembershipCard', {
        [`${carouselItemClass}--selected`]: isSelected,
        [`${carouselItemClass}--blocked`]: isBlocked
      })}
    >
      <div>
        <div className='MembershipCard__body'>
          <div className='MembershipCard__body__title'>
            <h5 className='MembershipTitle mt-2'>{item?.title || item?.name}</h5>
            <div className='amount mt-4'>
              <span className='price h3 small__price_font'>{price} {item?.currency} / </span>
              <span className='recurrence'>
                <FormattedMessage
                  id={`MembershipSelectionPage.recurrence.${item?.recurrence}`}
                  defaultMessage={'mes'}
                />
              </span>
            </div>
          </div>
          <p
            className={classNames('description', { 'description--compact': hasCompactDescription })}
            title={item.metadata?.description && item.metadata?.description.map((description, i) => description)}
          >
          {item?.metadata?.description && item?.metadata?.description.map((description, i) => description)}
          </p>
        <div className="broadcasting">
          {(item.broadcastingDates?.start || item.broadcastingDates?.end) && (
            <dl>
              {item.broadcastingDates?.start && (
                <>
                  <dt><FormattedMessage id="Carrousel.item.broadcasting.start"/></dt>
                  <dd>{new Date(item.broadcastingDates?.start).toLocaleString()}</dd>
                </>
              )}
              {item.broadcastingDates?.end && (
                <>
                  <dt><FormattedMessage id="Carrousel.item.broadcasting.end"/></dt>
                  <dd>{new Date(item.broadcastingDates?.end).toLocaleString()}</dd>
                </>
              )}
            </dl>
          )}
        </div>
        </div>
        <div className="carouselItem__footer footer mx-4">
          <Button
            active={isSelected}
            text={isSelected ? translate(intl, 'added') : translate(intl, 'add')}
            onClick={() => {
              if (!isBlocked) {
                onClick(item)
              }
            }}
          />
        </div>
      </div>
    </Card>
  )
}

export const PlanItem = ({
  price,
  plan,
  onClick,
  apportionmentConfig,
  applyTrial,
  availableSalePriceOffer,
  active,
  disabled,
  allProducts,
  client,
  userCountry
}) => {
  const ref = React.createRef();
  const [showMore, setShowMore] = React.useState(false);
  const [showLink, setShowLink] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollHeight > el.clientHeight) {
      setShowLink(true);
    }
  }, [ref, plan]);

  const onClickMore = () => {
    setShowMore(!showMore);
  };

  const mainClass = classNames(planClass, {
    [`${planClass}--active`]: active
  });

  return (
    <Card className={mainClass}>
      <div>

        <div className="MembershipCard__body">
          <div className="MembershipCard__body__title">
            <h5 className="MembershipTitle mt-2">{isEssentialPlan(plan) ?  `${plan?.name} *` : plan?.name}</h5>
            <div className="amount mt-4">
              <span className="price h3">{price} / </span>
              {plan.recurrence && (
                <span className="recurrence">
                  <FormattedMessage
                    id={`MembershipSelectionPage.recurrence.${plan?.recurrence}`}
                    defaultMessage={plan.recurrence}
                  />
                </span>
              )}
            </div>
            {applyTrial && (
              <>
                <span>
                  <FormattedMessage
                    id="MembershipSelectionPage.freeTrial"
                  />
                </span>
                <span className="recurrence">
                  <span style={{ marginRight: '2px' }}>{plan.trial.amount}</span>
                  {plan.trial.amount === 1 ? (
                    <FormattedMessage
                      id={`MembershipSelectionPage.addOn.trial.${plan.trial.unit}.one`}
                      values={{
                        unit: plan.trial?.unit ?? 'month'
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      id={`MembershipSelectionPage.addOn.trial.${plan.trial.unit}`}
                      values={{
                        unit: plan.trial?.unit ?? 'month'
                      }}
                    />
                  )}
                </span>
              </>
            )}
            {
              !applyTrial && availableSalePriceOffer && (
                <>
                  <span>
                    <FormattedMessage
                      id="MembershipSelectionPage.summary.discountAmount"
                    />
                    {availableSalePriceOffer?.discountType === 'percentage' && ` (${availableSalePriceOffer?.amount}%)`}
                    {`: ${formatPricesByCountry(plan?.grossPriceToShow - plan?.priceToShow, userCountry)}`}
                  </span>
                  <span>
                    <FormattedMessage id="MembershipSelectionPage.summary.salePrice.until" />
                    {`: ${format(new Date(availableSalePriceOffer.to.split('T')[0]), 'dd/MM/yyyy')}`}
                  </span>
                  <div className="amount">
                    <span className="price">
                      {formatPricesByCountry(plan?.priceToShow, userCountry)} /
                    </span>
                    {plan.recurrence && (
                      <span className="recurrence">
                        <FormattedMessage
                          id={`MembershipSelectionPage.recurrence.${plan.recurrence}`}
                          defaultMessage={plan.recurrence}
                        />
                      </span>
                    )}
                  </div>
                </>
              )
            }
          </div>
          <ul ref={ref} className="description" style={showMore ? { height: 'auto' } : {}}>
            {
              plan?.rules?.[RulesType.PURCHASE]?.[RulesName.PLAN_INCLUDE_ADDONS]?.map((includedAddOn, index) => {
                const productInfo = allProducts?.find(p => p.productID === includedAddOn.productId);
                if (!productInfo) {
                  return null;
                }

                return (
                  <li key={index}>
                    <div className="description__item">
                      <div className="icon">
                        <FaRegCheckCircle color="#00AEED" />
                      </div>
                      <div className="text">
                        <span className="description__item-text">
                          <FormattedMessage
                            id="MembershipSelectionPage.membership.includeAddonInPlan"
                            values={{
                              name: productInfo.name
                            }}
                          />
                        </span>
                      </div>
                    </div>
                  </li>
                )
              })
            }
            {Array.isArray(plan.metadata?.description) &&
              plan?.metadata?.description.map((desc) => (
                <li key={desc}>
                  <div className="description__item">
                    <div className="icon mr-3">
                      <FaRegCheckCircle color="#00AEED" />
                    </div>
                    <div className="text">
                      <span className="description__item-text text-secondary">
                        {desc}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
          </ul>
          <div className="more-arrow-down-contianer">
            {showLink ? (
              <div className={`more-arrow-down ${showMore ? 'more-arrow-turn-up' : ''}`} onClick={onClickMore} />
            ) : (
              <div className="more-arrow-down-square" />
            )}
          </div>
          <div>
            <div className="MembershipCard__footer">
              <Button
                disabled={disabled}
                onClick={() => {
                  onClick(plan)
                }}
                text={<FormattedMessage id="MembershipSelectionPage.contract" />}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
};

PlanItem.propTypes = {
  price: PropTypes.string,
  plan: PropTypes.object,
  onClick: PropTypes.func,
  applyTrial: PropTypes.bool,
  apportionmentConfig: PropTypes.object,
  availableSalePriceOffer: PropTypes.object,
  active: bool,
  disabled: PropTypes.bool,
  allProducts: PropTypes.array,
  client: PropTypes.string.isRequired,
  userCountry: PropTypes.string,
};

AddOnItem.propTypes = {
  price: PropTypes.string,
  intl: PropTypes.object,
  client: PropTypes.string.isRequired,
  applyTrial: PropTypes.bool,
  item: PropTypes.object.isRequired,
  availableSalePriceOffer: PropTypes.object,
  onClick: PropTypes.func,
};
